import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import { TOUR_DEMO_CONTROL, type TourDemoControl } from "./constants";
import { findDemoConditionIndex } from "./demoCondition";
import { CONDITION_EDITOR_STEP_IDS, TOUR_STEP_ID } from "./segmentBuilderTour";

import { OperatorValue } from "@/config/operatorConfig";
import { createEmptyCondition, createEmptyRule } from "@/features/segments/builder/mappers";
import type { FormCondition, FormRule, SegmentFormValues } from "@/features/segments/builder/schemas";
import type { SegmentField } from "@/features/segments/types";
import { useTour } from "@/features/tours/useTour";

type TourDemoState = {
  conditionKey: string | null;
  control: TourDemoControl | null;
};

const CLOSED: TourDemoState = { conditionKey: null, control: null };

const STEP_CONTROL: Partial<Record<string, TourDemoControl>> = {
  [TOUR_STEP_ID.FIELD]: TOUR_DEMO_CONTROL.FIELD,
  [TOUR_STEP_ID.OPERATORS]: TOUR_DEMO_CONTROL.OPERATOR,
  [TOUR_STEP_ID.VALUES]: TOUR_DEMO_CONTROL.VALUE,
};

const pickExampleField = (fields: SegmentField[]) =>
  fields
    .filter((field) => field.operators.includes(OperatorValue.IS_ANY_OF))
    .reduce<SegmentField | undefined>(
      (best, field) => (best && best.operators.length >= field.operators.length ? best : field),
      undefined,
    );

const createExampleCondition = (field: SegmentField): FormCondition => ({
  ...createEmptyCondition(),
  field: field.value,
  operator: OperatorValue.IS_ANY_OF,
});

const withDemoCondition = (rules: FormRule[], fields: SegmentField[]): FormRule[] => {
  const [firstRule, ...otherRules] = rules;
  if (findDemoConditionIndex(firstRule, fields) !== -1) return rules;
  const field = pickExampleField(fields);
  if (!field) return rules;
  const example = createExampleCondition(field);
  if (!firstRule) return [{ ...createEmptyRule(), conditions: [example] }];
  return [{ ...firstRule, conditions: [...firstRule.conditions, example] }, ...otherRules];
};

export function useSegmentTourDemo(form: UseFormReturn<SegmentFormValues>, fields: SegmentField[], readOnly: boolean) {
  const { isRunning } = useTour();
  const { getValues, setValue, clearErrors } = form;
  const [demo, setDemo] = useState<TourDemoState>(CLOSED);
  const demoConditionIndex = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!isRunning || readOnly) return;
    const current = getValues("rules");
    const snapshot = structuredClone(current);
    const rules = withDemoCondition(current, fields);
    if (rules !== current) setValue("rules", rules);
    const index = findDemoConditionIndex(rules[0], fields);
    demoConditionIndex.current = index === -1 ? null : index;

    return () => {
      setValue("rules", snapshot);
      clearErrors("rules");
      demoConditionIndex.current = null;
      setDemo(CLOSED);
    };
  }, [isRunning, readOnly, fields, getValues, setValue, clearErrors]);

  const applyStep = useCallback(
    (stepId: string) => {
      const index = demoConditionIndex.current;
      if (index === null) {
        setDemo(CLOSED);
        return;
      }
      const path = `rules.0.conditions.${index}.isInverted` as const;
      const isInverted = stepId === TOUR_STEP_ID.EXCLUDE;
      if (getValues(path) !== isInverted) setValue(path, isInverted);
      if (!CONDITION_EDITOR_STEP_IDS.has(stepId)) {
        setDemo(CLOSED);
        return;
      }
      const conditionKey = getValues(`rules.0.conditions.${index}.clientKey`);
      const control = STEP_CONTROL[stepId] ?? null;
      setDemo((current) =>
        current.conditionKey === conditionKey && current.control === control ? current : { conditionKey, control },
      );
    },
    [getValues, setValue],
  );

  return { ...demo, applyStep };
}
